import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Button,
  Container,
  Box,
  ColumnLayout,
  Spinner,
  Alert,
  ExpandableSection,
} from '@cloudscape-design/components';
import { useConfig, useConfigs } from '@features/configs';
import { useDependents } from '@features/configs/hooks/use-dependents';
import { useIsAdmin } from '@features/auth';
import { PendingDeletionWarning } from '@features/configs/components/PendingDeletionWarning';
import { MenuDetail } from '@features/configs/components/detail/MenuDetail';
import { QueueDetail } from '@features/configs/components/detail/QueueDetail';
import { DnisDetail } from '@features/configs/components/detail/DnisDetail';
import { IntentDetail } from '@features/configs/components/detail/IntentDetail';
import { BusinessHoursDetail } from '@features/configs/components/detail/BusinessHoursDetail';
import { HolidayDetail } from '@features/configs/components/detail/HolidayDetail';
import { UsedBySection } from '@features/configs/components/detail/UsedBySection';
import { formatDate } from '@features/configs/components/detail/format-utils';
import { exportConfigAsCsv, exportConfigAsPdf } from '@features/configs/utils/export-config';

/**
 * Human-readable configuration detail view.
 * Replaces raw JSON with type-specific structured summary cards.
 * BR-11: No raw JSON as primary content, no camelCase labels, no ISO timestamps.
 */
export function ConfigDetailPage() {
  const { configId } = useParams<{ configId: string }>();
  const [searchParams] = useSearchParams();
  const configType = searchParams.get('type') ?? undefined;
  const navigate = useNavigate();
  const { data: config, isLoading, error } = useConfig(configId, configType);
  const { data: allConfigsData } = useConfigs();
  const { data: dependentsData, isLoading: dependentsLoading } = useDependents(configId);
  const { isAdmin } = useIsAdmin();

  const allConfigs = allConfigsData?.items ?? [];

  if (isLoading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  if (error || !config) {
    return (
      <Alert type="error">
        {error?.message ?? 'Configuration not found.'}
      </Alert>
    );
  }

  const formattedJson = JSON.stringify(config, null, 2);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => navigate('/')}>Back to list</Button>
              <Button onClick={() => exportConfigAsCsv(config)} iconName="download">Export CSV</Button>
              <Button onClick={() => exportConfigAsPdf(config)} iconName="file">Export PDF</Button>
              <Button variant="primary" onClick={() => navigate(`/configs/${configId}/edit?type=${config.configType}`)}>Edit</Button>
              {isAdmin && (
                <Button onClick={() => navigate(`/?delete=${configId}`)}>Delete</Button>
              )}
            </SpaceBetween>
          }
        >
          {config.configName}
        </Header>
      }
    >
      <SpaceBetween size="l">

        {/* BR-16: Warning if this config references a pending-deletion config */}
        <PendingDeletionWarning config={config} allConfigs={allConfigs} />

        {/* Configuration Summary — type-specific human-readable fields */}
        <Container header={<Header variant="h2">Configuration Summary</Header>}>
          <SpaceBetween size="l">
            {config.configType === 'menus' && (
              <MenuDetail config={config} allConfigs={allConfigs} />
            )}
            {config.configType === 'queues' && (
              <QueueDetail config={config} allConfigs={allConfigs} />
            )}
            {config.configType === 'dnis' && (
              <DnisDetail config={config} allConfigs={allConfigs} />
            )}
            {config.configType === 'intents' && (
              <IntentDetail config={config} allConfigs={allConfigs} />
            )}
            {config.configType === 'business-hours' && (
              <BusinessHoursDetail config={config} />
            )}
            {config.configType === 'holidays' && (
              <HolidayDetail config={config} />
            )}
            {!['menus', 'queues', 'dnis', 'intents', 'business-hours', 'holidays'].includes(config.configType) && (
              <Box color="text-status-inactive">
                Detailed view for "{config.configType}" type coming soon.
              </Box>
            )}
          </SpaceBetween>
        </Container>

        {/* Metadata — human-readable dates */}
        <Container header={<Header variant="h2">Details</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Last Modified</Box>
              <div>{formatDate(config.lastModified)}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Modified By</Box>
              <div>{config.lastModifiedBy}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Version</Box>
              <div>{config.version}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Type</Box>
              <div>{config.configType}</div>
            </div>
          </ColumnLayout>
        </Container>

        {/* Used By section */}
        <Container header={<Header variant="h2">Used By</Header>}>
          <UsedBySection dependentsData={dependentsData} isLoading={dependentsLoading} />
        </Container>

        {/* Raw JSON — collapsed by default, for developers only */}
        <ExpandableSection
          headerText="Technical Data (for developers)"
          variant="footer"
        >
          <pre
            style={{
              backgroundColor: '#f8f9fa',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '500px',
              fontSize: '12px',
              fontFamily: 'IBM Plex Mono, monospace',
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {formattedJson}
          </pre>
        </ExpandableSection>
      </SpaceBetween>
    </ContentLayout>
  );
}
